
import 'isomorphic-fetch'
import React, { Component } from 'react'
import PropTypes from 'prop-types'

import Map from 'react-map-gl'

import WebMercatorViewport from 'viewport-mercator-project'
import bbox from '@turf/bbox'
import { point, featureCollection } from '@turf/helpers'
import { addMapboxCss } from 'Utils/mapUtils'
import { nearbyPlacesGroup } from 'Layout/PhotosAndLocationModal/data'
import bugsnagClient from 'Root/bugSnagClient'
import { MAPBOX_API_KEY, MAPBOX_STYLE_KEY } from 'Config/config'
import { Marker } from 'Components/MapBox'


import PlaceIcon from '../PlaceIcon'
import TransitIcon from '../../../../../containers/layout/PhotosAndLocationModal/components/TransitIcon'

import {
  _MapWidthPopup,
  _CurrentMarker,
} from './styles'


const mapBoxDefaultStyle = 'https://api.mapbox.com/styles/v1/property-ca/' +
  `${MAPBOX_STYLE_KEY}?access_token=${MAPBOX_API_KEY}`

class IntroMap extends Component {
  constructor(props) {
    super(props)
    const { currentAddress } = props
    const currentAddressGeoJsonPoint = currentAddress &&
      point([currentAddress.lng, currentAddress.lat], { name: 'Current Address' })

    this.state = {
      viewport: {
        latitude: currentAddress.lat,
        longitude: currentAddress.lng,
        zoom: 15,
      },
      // defaultMapStyle: null,
      mapStyle: null,
      currentAddressGeoJsonPoint,
      schoolMarkers: null,
      placesMarkersHtml: null,
      groceryMarkers: null,
      restaurantMarkers: null,
      // transitMakersHtml: null,
    }

    fetch(mapBoxDefaultStyle)
      .then(res => {
        try {
          const styleJson = res.json()
          return styleJson
        } catch(e) {
          // eslint-disable-next-line prefer-promise-reject-errors
          Promise.reject({ body: res, type: 'unparsable' })
        }
      })
      .catch(e => { bugsnagClient.notify('Mapbox Style Fetch Error:', e) })
      .then(styleJson => {
        this.setState({ mapStyle: styleJson })
      })
    this.loadMarkers = this.loadMarkers.bind(this)
  }


  componentDidMount() {
    this.loadMarkers({ transitMarkers: this.props?.transitMarkers, places: this.props?.places })
  }

  componentDidUpdate(prevProps) {
    // Inorder to cache the marker htmls to improve map performance
    if(this.props.transitMarkers && this.props.transitMarkers !== prevProps.transitMarkers) {
      this.loadMarkers({ transitMarkers: this.props.transitMarkers })
    }
    if(this.props.places && this.props.places !== prevProps.places && !this.state.placesMarkersHtml) {
      this.loadMarkers({ places: this.props.places })
    }
  }

  loadMarkers({ transitMarkers, places }) {
    const boundPointsArr = [this.state.currentAddressGeoJsonPoint]
    if(transitMarkers) {
      const transitMarkersHtml = []
      Object.values(transitMarkers).forEach((transitMarkerArray) => {
        transitMarkerArray.forEach((transitMarker) => {
          boundPointsArr.push(
            point([transitMarker.lng, transitMarker.lat]),
          )
          transitMarkersHtml.push(
            <Marker
              key={transitMarker.id}
              lat={transitMarker.lat}
              lng={transitMarker.lng}
            >
              <TransitIcon
                transitType={transitMarker.vehicleType}
                size={30}
              />
            </Marker>,
          )
        })
      })

      this.fitBounds(boundPointsArr)
      // eslint-disable-next-line
      this.setState({ transitMarkersHtml })
    }

    if(places) {
      // create dictionary with place ids to access on hover
      const restaurantMarkers = []
      const groceryMarkers = []
      const schoolMarkers = []
      const placesMarkersHtml = places.map(place => {
        const geoLoc = place._geoloc || {}
        boundPointsArr.push(point([geoLoc.lng, geoLoc.lat], { name: place.name }))

        const isBarsRestos =
          nearbyPlacesGroup.restaurant.facetFilters.includes(place.category)
        const isGrocery =
          nearbyPlacesGroup.grocery.facetFilters.includes(place.category)
        const isSchool = !place.category
        const marker = (
          <Marker
            key={`place-${place.id}`}
            lat={place._geoloc.lat}
            lng={place._geoloc.lng}
          >
            <PlaceIcon
              placeType={
                'amenities-marker' +
                (isBarsRestos ? '-bars-restos' :
                  isGrocery ? '-grocery' :
                    isSchool ? '-school' : '')
              }
              size={30}
            />
          </Marker>
        )
        if(isBarsRestos) {
          restaurantMarkers.push(marker)
        } else if(isGrocery) {
          groceryMarkers.push(marker)
        } else if(isSchool) {
          schoolMarkers.push(marker)
        }
        return (
          <Marker
            key={`place-${place.id}`}
            lat={place._geoloc.lat}
            lng={place._geoloc.lng}
          >
            <PlaceIcon
              placeType={
                'amenities-marker' +
                (isBarsRestos ? '-bars-restos' :
                  isGrocery ? '-grocery' :
                    isSchool ? '-school' : '')
              }
              size={30}
            />
          </Marker>
        )
      })
      this.fitBounds(boundPointsArr)
      // eslint-disable-next-line
      this.setState({
        placesMarkersHtml, schoolMarkers, restaurantMarkers, groceryMarkers })
    }
  }


  fitBounds(boundPointsArr) {
    if(boundPointsArr.length > 1) {
      const { winWidth } = this.props
      if(winWidth <= 767) return
      const padding = { top: 40, right: 80, bottom: 160, left: 80 }

      const minMaxPoint = bbox(featureCollection(boundPointsArr))

      const [minLng, minLat, maxLng, maxLat] = minMaxPoint
      try {
        const { width, height } = document.querySelector('#mapWithPopups').getBoundingClientRect()
        if(!width || !height) return
        // if side paddings are bigger than width or height remove paddings
        const viewPortOptions = { padding }
        if(width <= (padding.left + padding.right) || height <= (padding.top + padding.bottom)) {
          viewPortOptions.padding = 0
        }

        const viewport = new WebMercatorViewport({ ...this.state.viewport, width, height })
        const { longitude, latitude } =
          viewport.fitBounds([[minLng, minLat], [maxLng, maxLat]], viewPortOptions)
        this.setState({
          viewport: {
            ...this.state.viewport,
            width,
            height,
            longitude,
            latitude,
          },
        })
      } catch(e) {
        bugsnagClient.notify(e)
      }
    }
  }

  render() {
    const { currentAddress } = this.props
    const {
      mapStyle, transitMarkersHtml, schoolMarkers, groceryMarkers, restaurantMarkers,
    } = this.state
    return (
      <_MapWidthPopup id="mapWithPopups">
        <Map
          initialViewState={{ ...this.state.viewport }}
          width={'100%'}
          height={'100%'}
          mapboxAccessToken={MAPBOX_API_KEY}
          mapStyle={mapStyle}
          onLoad={addMapboxCss}
          // onNativeClick doesn't have the 300ms delay of normal click
          // reuseMaps disables the show route info on hover feature
          reuseMaps
          dragRotate={false}
          fog={undefined}
        >
          <_CurrentMarker
            lat={currentAddress.lat}
            lng={currentAddress.lng}
          />
          {schoolMarkers}
          {transitMarkersHtml}
          {groceryMarkers}
          {restaurantMarkers}
        </Map>
      </_MapWidthPopup>
    )
  }
}


IntroMap.propTypes = {
  places: PropTypes.arrayOf(PropTypes.shape({})),
  currentAddress: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    street_number: PropTypes.string,
    street_name: PropTypes.string,
    title: PropTypes.string,
  }),
  transitMarkers: PropTypes.shape({}),
  winWidth: PropTypes.number,
}

export default IntroMap
